import type { DefaultOptionType } from "@braze/beacon-lab-select";

import type { I18nT } from "@lib/dashboardI18n";

import countriesData from "../utils/CountryInfo.json";
import { createCountryOptions } from "../utils/sortCountriesByLocale";
import type { BusinessDetailsSelectFields } from "./types";

export const fieldOptions: (
  t: I18nT<"free-trial-registration">,
  l: I18nT<"country-labels">,
  locale?: string
) => Record<keyof BusinessDetailsSelectFields, DefaultOptionType<string>[]> = (t, l, locale = "en") => ({
  industry: [
    {
      value: "Education",
      label: t("industry-option-education", "Education"),
    },
    {
      value: "Financial Services",
      label: t("industry-option-financial-services", "Financial Services"),
    },
    {
      value: "Government & Non-Profit",
      label: t("industry-option-government-and-non-profit", "Government & Non-Profit"),
    },
    {
      value: "Healthcare & Life Sciences",
      label: t("industry-option-healthcare-and-life-sciences", "Healthcare & Life Sciences"),
    },
    {
      value: "Logistics & Utilities",
      label: t("industry-option-logistics-and-utilities", "Logistics & Utilities"),
    },
    {
      value: "Manufacturing",
      label: t("industry-option-manufacturing", "Manufacturing"),
    },
    {
      value: "Media & Entertainment",
      label: t("industry-option-media-and-entertainment", "Media & Entertainment"),
    },
    {
      value: "Professional Services",
      label: t("industry-option-professional-services", "Professional Services"),
    },
    {
      value: "Restaurants & On-Demand",
      label: t("industry-option-restaurants-and-on-demand", "Restaurants & On-Demand"),
    },
    {
      value: "Retail & Consumer Goods",
      label: t("industry-option-retail-and-consumer-goods", "Retail & Consumer Goods"),
    },
    {
      value: "Technology",
      label: t("industry-option-technology", "Technology"),
    },
    {
      value: "Telecommunications",
      label: t("industry-option-telecommunications", "Telecommunications"),
    },
    {
      value: "Travel, Transportation, & Hospitality",
      label: t("industry-option-travel-transportation-and-hospitality", "Travel, Transportation, & Hospitality"),
    },
  ],
  features: [
    {
      value: "Braze Data Platform",
      label: t("feature-option-braze-data-platform", "Braze Data Platform"),
      description: t(
        "feature-option-braze-data-platform-description",
        "Unify and activate customer data from any source"
      ),
    },
    {
      value: "Journey Orchestration",
      label: t("feature-option-journey-orchestration", "Journey Orchestration"),
      description: t(
        "feature-option-journey-orchestration-description",
        "Orchestrate engaging customer journeys in Braze Canvas"
      ),
    },
    {
      value: "Cross-Channel Messaging",
      label: t("feature-option-cross-channel-messaging", "Cross-Channel Messaging"),
      description: t(
        "feature-option-cross-channel-messaging-description",
        "Engage customers on relevant channels & touchpoints"
      ),
    },
    {
      value: "Experimentation",
      label: t("feature-option-experimentation", "Experimentation"),
      description: t("feature-option-experimentation-description", "Test and optimize every part of the journey"),
    },
    {
      value: "Reporting & Analytics",
      label: t("feature-option-analytics", "Reporting & Analytics"),
      description: t(
        "feature-option-analytics-description",
        "Gain a holistic view of customer engagement performance"
      ),
    },
    {
      value: "BrazeAI™",
      label: t("feature-option-braze-ai", "BrazeAI™"),
      description: t(
        "feature-option-braze-ai-description",
        "Create, personalize, and optimize experiences with BrazeAI™"
      ),
    },
  ],
  channels: [
    {
      value: "Email",
      label: t("channel-option-email", "Email"),
    },
    {
      value: "LINE",
      label: t("channel-option-mobile-line", "LINE"),
    },
    {
      value: "Mobile Messaging",
      label: t("channel-option-mobile-messaging", "Mobile Messaging"),
    },
    {
      value: "SMS",
      label: t("channel-option-sms", "SMS"),
    },
    {
      value: "WhatsApp",
      label: t("channel-option-whatsapp", "WhatsApp"),
    },
    {
      value: "Web Messaging",
      label: t("channel-option-web-messaging", "Web Messaging"),
    },
  ],
  companySize: ["1 - 50", "51 - 99", "100 - 199", "200 - 749", "750 - 999", "1,000 - 1,999", "2,000+"].map(
    (companySize) => ({
      label: companySize,
      value: companySize,
    })
  ),
  country: createCountryOptions(countriesData, locale),

  goals: [
    {
      value: "Activate new customers",
      label: t("feature-option-activate-new-customers", "Activate new customers"),
      description: t(
        "feature-option-activate-new-customers-description",
        "Guide newly acquired customers to their first 'aha' moment"
      ),
    },
    {
      value: "Monetize more effectively",
      label: t("feature-option-monetize-more-effectively", "Monetize more effectively"),
      description: t(
        "feature-option-monetize-more-effectively-description",
        "Increase revenue by delivering relevant and timely campaigns"
      ),
    },
    {
      value: "Retain customers longer",
      label: t("feature-option-retain-customers-longer", "Retain customers longer"),
      description: t(
        "feature-option-retain-customers-longer-description",
        "Boost customer loyalty and LTV with lifecycle journeys"
      ),
    },
  ],
  usersCount: [
    {
      label: t("user-count-pre-launch", "Pre-Launch"),
      value: "Pre-Launch",
    },
    { label: t("user-count-under-10k", "Under 10k"), value: "Under 10k" },
    { label: t("user-count-10-50k", "10 - 50k"), value: "10 - 50k" },
    { label: t("user-count-50-100k", "50 - 100k"), value: "50 - 100k" },
    { label: t("user-count-100k-1mm", "100k - 1mm"), value: "100k - 1mm" },
    { label: t("user-count-1mm", "1mm+"), value: "1mm+" },
  ],
});
